import { z } from 'zod';
import { DataRule } from 'libs/end-to-end/src/data-rules';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { errResult } from 'libs/end-to-end/src/result';
import { okResult } from 'libs/end-to-end/src/result';
import { Result } from 'libs/end-to-end/src/result';
import { EmptyObject } from 'libs/end-to-end/src/generic';
import { ValidateEntitlementFeaturesError } from 'libs/end-to-end/src/types/errors/validate-entitlement-features-error';

export const EntitlementFeatureKeysWithoutOptionalFocusArea = [
  'pipelineAnalysis',
  'hiringCopilot',
  'promotionCopilot',
  'payBenchmarking',
  'payCorrection',
  'savedAnalysis',
  'dashboards',
  'rubricsRead',
  'rubricsManage',
  'shareDashboardsPublicly',
  'shareDashboards',
  'ai',
  'reports',
  'richText',
  'all',
] as const;

export const EntitlementFeatureKeysWithOptionalFocusArea = [
  'biasAnalysis',
  'binScatterMultiSelect',
  'solutions',
  'geolocation',
  'tableView',
  'summaryStats',
  'heatmap',
  'progress',
  'successFactors',
] as const;

// We don't need these anymore any will remove them eventually once we can check that there are not any in the DB.
export const EntitlementFeatureKeysDeprecated = [
  'forYou',
  'topOpportunities',
  'insights',
] as const;

export const EntitlementFeatureKeysWithOptionalFocusAreaSchema = z.enum(
  EntitlementFeatureKeysWithOptionalFocusArea,
);
export type EntitlementFeatureKeysWithOptionalFocusArea = z.infer<
  typeof EntitlementFeatureKeysWithOptionalFocusAreaSchema
>;

export const EntitlementFeatureKeys = [
  ...EntitlementFeatureKeysWithoutOptionalFocusArea,
  ...EntitlementFeatureKeysWithOptionalFocusArea,
  ...EntitlementFeatureKeysDeprecated,
] as const;

export const EntitlementFeatureKey = z.enum(EntitlementFeatureKeys);
export type EntitlementFeatureKey = z.infer<typeof EntitlementFeatureKey>;

export const EntitlementFeature = z.object({
  key: EntitlementFeatureKey,
  focusArea: FocusArea.nullable(),
});

export type EntitlementFeature = z.infer<typeof EntitlementFeature>;

export const AllFeatures: EntitlementFeature[] = [
  {
    key: 'all',
    focusArea: null,
  },
];

export const GroupEntitlement = z.object({
  databaseId: z.string(),
  dataRules: DataRule.array(),
  features: EntitlementFeature.array(),
});

export type GroupEntitlement = z.infer<typeof GroupEntitlement>;

export const GroupEntitlements = z.array(GroupEntitlement);

export type GroupEntitlements = z.infer<typeof GroupEntitlements>;

export const isEntitlementFeatureWithOptionalFocusArea = (
  key: EntitlementFeatureKey,
): key is EntitlementFeatureKeysWithOptionalFocusArea => {
  const strVersion: string[] = EntitlementFeatureKeysWithOptionalFocusArea.map(
    (key) => key,
  );
  return strVersion.includes(key);
};

export const isEntitlementFeatureDeprecated = (
  key: EntitlementFeatureKey,
): boolean => {
  for (const deprecatedKey of EntitlementFeatureKeysDeprecated) {
    if (key === deprecatedKey) {
      return true;
    }
  }

  return false;
};

export const validateEntitlementFeatures = (
  features: EntitlementFeature[],
): Result<EmptyObject, ValidateEntitlementFeaturesError> => {
  const anyAreAll = features.some((feature) => feature.key === 'all');

  if (anyAreAll && features.length > 1) {
    return errResult({
      errorType: 'ValidateEntitlementFeaturesDuplicateError',
      invalidIndexes: features.map((_, index) => index),
    });
  }

  // a duplicate is when the same key and focusArea is used more than once
  for (const feature of features) {
    if (feature.focusArea === null) {
      const otherFeatureWithANonNullFocusArea = features.find(
        (f) => f !== feature && f.key === feature.key && f.focusArea !== null,
      );

      if (otherFeatureWithANonNullFocusArea) {
        return errResult({
          errorType: 'ValidateEntitlementFeaturesDuplicateError',
          invalidIndexes: [
            features.indexOf(feature),
            features.indexOf(otherFeatureWithANonNullFocusArea),
          ],
        });
      }
    }

    // otherwise look for an exact match
    const otherFeature = features.find(
      (f) =>
        f.key === feature.key &&
        f.focusArea === feature.focusArea &&
        f !== feature,
    );

    if (otherFeature) {
      return errResult({
        errorType: 'ValidateEntitlementFeaturesDuplicateError',
        invalidIndexes: [
          features.indexOf(feature),
          features.indexOf(otherFeature),
        ],
      });
    }
  }

  return okResult({});
};

const isEntitledToAllFeatures = ({
  features,
}: {
  features: EntitlementFeature[];
}) => features.some((feature) => feature.key === 'all');

const isEntitledToFeatureInAnyFocusArea = ({
  key,
  features,
}: {
  key: EntitlementFeatureKey;
  features: EntitlementFeature[];
}) =>
  isEntitledToAllFeatures({ features }) ||
  features.some((feature) => feature.key === key);

const isEntitledToFeatureInFocusArea = ({
  key,
  features,
  focusArea,
}: {
  key: EntitlementFeatureKeysWithOptionalFocusArea;
  focusArea: FocusArea;
  features: EntitlementFeature[];
}) =>
  isEntitledToAllFeatures({ features }) ||
  features.some(
    (feature) =>
      feature.key === key &&
      (feature.focusArea === null || feature.focusArea === focusArea),
  );

export const isEntitledToBinScatterMultiSelect = ({
  features,
  focusArea,
}: {
  features: EntitlementFeature[];
  focusArea: FocusArea;
}) =>
  isEntitledToFeatureInFocusArea({
    key: 'binScatterMultiSelect',
    features,
    focusArea,
  });

export const isEntitledToBiasAnalysis = ({
  features,
  focusArea,
}: {
  features: EntitlementFeature[];
  focusArea: FocusArea | null;
}) => {
  if (!focusArea) {
    return isEntitledToFeatureInAnyFocusArea({
      key: 'biasAnalysis',
      features,
    });
  }

  return isEntitledToFeatureInFocusArea({
    key: 'biasAnalysis',
    features,
    focusArea,
  });
};

export const isEntitledToDashboards = ({
  features,
}: {
  features: EntitlementFeature[];
}) =>
  isEntitledToFeatureInAnyFocusArea({
    key: 'dashboards',
    features,
  });

export const isEntitledToRubricsManage = ({
  features,
}: {
  features: EntitlementFeature[];
}) =>
  isEntitledToFeatureInAnyFocusArea({
    key: 'rubricsManage',
    features,
  });

export const isEntitledToRubricsRead = ({
  features,
}: {
  features: EntitlementFeature[];
}) =>
  isEntitledToFeatureInAnyFocusArea({
    key: 'rubricsRead',
    features,
  }) || isEntitledToRubricsManage({ features });

export const isEntitledToPipelineAnalysis = ({
  features,
}: {
  features: EntitlementFeature[];
}) =>
  isEntitledToFeatureInAnyFocusArea({
    key: 'pipelineAnalysis',
    features,
  });

export const isEntitledToHiringCopilot = ({
  features,
}: {
  features: EntitlementFeature[];
}) =>
  isEntitledToFeatureInAnyFocusArea({
    key: 'hiringCopilot',
    features,
  });

export const isEntitledToPromotionCopilot = ({
  features,
}: {
  features: EntitlementFeature[];
}) =>
  isEntitledToFeatureInAnyFocusArea({
    key: 'promotionCopilot',
    features,
  });

export const isEntitledToSavedAnalysis = ({
  features,
}: {
  features: EntitlementFeature[];
}) =>
  isEntitledToFeatureInAnyFocusArea({
    key: 'savedAnalysis',
    features,
  });

export const isEntitledToReports = ({
  features,
}: {
  features: EntitlementFeature[];
}) =>
  isEntitledToFeatureInAnyFocusArea({
    key: 'reports',
    features,
  });

export const isEntitledToPayBenchmarking = ({
  features,
}: {
  features: EntitlementFeature[];
}) =>
  isEntitledToFeatureInAnyFocusArea({
    key: 'payBenchmarking',
    features,
  });

export const isEntitledToPayCorrection = ({
  features,
}: {
  features: EntitlementFeature[];
}) =>
  isEntitledToFeatureInAnyFocusArea({
    key: 'payCorrection',
    features,
  });

export const isEntitledToHeatmap = ({
  features,
  focusArea,
}: {
  features: EntitlementFeature[];
  focusArea: FocusArea | null;
}) => {
  if (!focusArea) {
    return isEntitledToFeatureInAnyFocusArea({
      key: 'heatmap',
      features,
    });
  }

  return isEntitledToFeatureInFocusArea({
    key: 'heatmap',
    features,
    focusArea,
  });
};

export const isEntitledToProgress = ({
  features,
  focusArea,
}: {
  features: EntitlementFeature[];
  focusArea: FocusArea | null;
}) => {
  if (!focusArea) {
    return isEntitledToFeatureInAnyFocusArea({
      key: 'progress',
      features,
    });
  }

  return isEntitledToFeatureInFocusArea({
    key: 'progress',
    features,
    focusArea,
  });
};

export const isEntitledToSuccessFactors = ({
  features,
  focusArea,
}: {
  features: EntitlementFeature[];
  focusArea: FocusArea | null;
}) => {
  if (!focusArea) {
    return isEntitledToFeatureInAnyFocusArea({
      key: 'successFactors',
      features,
    });
  }

  return isEntitledToFeatureInFocusArea({
    key: 'successFactors',
    features,
    focusArea,
  });
};

export const isEntitledToGeolocation = ({
  features,
  focusArea,
}: {
  features: EntitlementFeature[];
  focusArea: FocusArea | null;
}) => {
  if (!focusArea) {
    return isEntitledToFeatureInAnyFocusArea({
      key: 'geolocation',
      features,
    });
  }

  return isEntitledToFeatureInFocusArea({
    key: 'geolocation',
    features,
    focusArea,
  });
};

export const isEntitledToRichText = ({
  features,
}: {
  features: EntitlementFeature[];
}) =>
  isEntitledToFeatureInAnyFocusArea({
    key: 'richText',
    features,
  });

export const isEntitledToCopilots = ({
  features,
}: {
  features: EntitlementFeature[];
}) =>
  isEntitledToHiringCopilot({ features }) ||
  isEntitledToPromotionCopilot({ features }) ||
  isEntitledToPayCorrection({ features }) ||
  isEntitledToPayBenchmarking({ features });

export const isEntitledToSolutions = ({
  features,
  focusArea,
}: {
  features: EntitlementFeature[];
  focusArea: FocusArea | null;
}) => {
  if (!focusArea) {
    return isEntitledToFeatureInAnyFocusArea({
      key: 'solutions',
      features,
    });
  }

  return isEntitledToFeatureInFocusArea({
    key: 'solutions',
    features,
    focusArea,
  });
};

export const isEntitledToTableView = ({
  features,
  focusArea,
}: {
  features: EntitlementFeature[];
  focusArea: FocusArea | null;
}) => {
  if (!focusArea) {
    return isEntitledToFeatureInAnyFocusArea({
      key: 'tableView',
      features,
    });
  }

  return isEntitledToFeatureInFocusArea({
    key: 'tableView',
    features,
    focusArea,
  });
};

export const isEntitledToSummaryStats = ({
  features,
  focusArea,
}: {
  features: EntitlementFeature[];
  focusArea: FocusArea | null;
}) => {
  if (!focusArea) {
    return isEntitledToFeatureInAnyFocusArea({
      key: 'summaryStats',
      features,
    });
  }

  return isEntitledToFeatureInFocusArea({
    key: 'summaryStats',
    features,
    focusArea,
  });
};

export const isEntitledToPublishDashboards = ({
  features,
}: {
  features: EntitlementFeature[];
}) => {
  return isEntitledToFeatureInAnyFocusArea({
    key: 'shareDashboardsPublicly',
    features,
  });
};

export const isEntitledToAi = ({
  features,
}: {
  features: EntitlementFeature[];
}) => {
  return isEntitledToFeatureInAnyFocusArea({
    key: 'ai',
    features,
  });
};

export const OrganizationGroupSelection = z.object({
  name: z.string(),
  id: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  organizationId: z.string(),
  description: z.string().nullable(),
});

export type OrganizationGroupSelection = z.infer<
  typeof OrganizationGroupSelection
>;

const OrganizationSelection = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  logoMimeType: z.string().nullable(),
  logoUrl: z.string().nullable(),
});

export const OrganizationGroupSelectionWithOrganization =
  OrganizationGroupSelection.extend({
    organization: OrganizationSelection,
  });

export type OrganizationGroupSelectionWithOrganization = z.infer<
  typeof OrganizationGroupSelectionWithOrganization
>;

export const OrganizationGroupSelections = z.object({
  groups: z.array(OrganizationGroupSelectionWithOrganization),
});

export type OrganizationGroupSelections = z.infer<
  typeof OrganizationGroupSelections
>;

export const OrganizationGroupInsertion = z.object({
  name: z.string(),
  organizationId: z.string(),
  id: z.string().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  description: z.string().nullable().optional(),
});

export type OrganizationGroupInsertion = z.infer<
  typeof OrganizationGroupInsertion
>;

export const OrganizationGroupUpdate = OrganizationGroupInsertion.partial();

export type OrganizationGroupUpdate = z.infer<typeof OrganizationGroupUpdate>;

export const AddUserToGroupRequest = z.object({
  organizationUserId: z.string().uuid(),
});

export type AddUserToGroupRequest = z.infer<typeof AddUserToGroupRequest>;

export const OrganizationGroupEntitlementSelection = z.object({
  id: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  organizationGroupId: z.string(),
  databaseId: z.string(),
});

export type OrganizationGroupEntitlementSelection = z.infer<
  typeof OrganizationGroupEntitlementSelection
>;

export const OrganizationGroupEntitlementFeatureSelection = z.intersection(
  z.object({
    id: z.string(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    organizationGroupEntitlementId: z.string(),
  }),
  EntitlementFeature,
);

export type OrganizationGroupEntitlementFeatureSelection = z.infer<
  typeof OrganizationGroupEntitlementFeatureSelection
>;

export const OrganizationGroupEntitlementDataRuleSelection = z.object({
  id: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  description: z.string().nullable(),
  organizationGroupEntitlementId: z.string(),
  rule: DataRule.nullable(),
});

export type OrganizationGroupEntitlementDataRuleSelection = z.infer<
  typeof OrganizationGroupEntitlementDataRuleSelection
>;

export const OrganizationGroupEntitlementSelectionWithFeaturesAndDataRules =
  OrganizationGroupEntitlementSelection.extend({
    features: z.array(OrganizationGroupEntitlementFeatureSelection),
    dataRules: z.array(OrganizationGroupEntitlementDataRuleSelection),
  });

export type OrganizationGroupEntitlementSelectionWithFeaturesAndDataRules =
  z.infer<typeof OrganizationGroupEntitlementSelectionWithFeaturesAndDataRules>;

export const GroupEntitlementResponse = z.object({
  entitlements: z.array(
    OrganizationGroupEntitlementSelectionWithFeaturesAndDataRules,
  ),
});

export type GroupEntitlementResponse = z.infer<typeof GroupEntitlementResponse>;

export const OrganizationGroupEntitlementInsertion = z.object({
  organizationGroupId: z.string(),
  databaseId: z.string(),
  id: z.string().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
});

export type OrganizationGroupEntitlementInsertion = z.infer<
  typeof OrganizationGroupEntitlementInsertion
>;

export const OrganizationGroupEntitlementFeatureInsertion =
  EntitlementFeature.extend({
    organizationGroupEntitlementId: z.string(),
    id: z.string().optional(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });

export type OrganizationGroupEntitlementFeatureInsertion = z.infer<
  typeof OrganizationGroupEntitlementFeatureInsertion
>;

export const OrganizationGroupEntitlementDataRuleInsertion = z.object({
  organizationGroupEntitlementId: z.string(),
  id: z.string().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  rule: DataRule,
});

export type OrganizationGroupEntitlementDataRuleInsertion = z.infer<
  typeof OrganizationGroupEntitlementDataRuleInsertion
>;

export const OrganizationGroupEntitlementInsertionWithFeaturesAndDataRules =
  OrganizationGroupEntitlementInsertion.extend({
    features: z.array(
      OrganizationGroupEntitlementFeatureInsertion.omit({
        organizationGroupEntitlementId: true,
      }),
    ),
    dataRules: z.array(
      OrganizationGroupEntitlementDataRuleInsertion.omit({
        organizationGroupEntitlementId: true,
      }),
    ),
  });

export type OrganizationGroupEntitlementInsertionWithFeaturesAndDataRules =
  z.infer<typeof OrganizationGroupEntitlementInsertionWithFeaturesAndDataRules>;

export const SetGroupEntitlementsRequest = z.object({
  entitlements: z.array(
    OrganizationGroupEntitlementInsertionWithFeaturesAndDataRules,
  ),
});

export type SetGroupEntitlementsRequest = z.infer<
  typeof SetGroupEntitlementsRequest
>;

export const MinimalUserSelection = z.object({
  id: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  email: z.string(),
});

export type MinimalUserSelection = z.infer<typeof MinimalUserSelection>;

export const OrganizationUserSelection = z.object({
  id: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  organizationId: z.string(),
  userId: z.string(),
  user: MinimalUserSelection,
});

export type OrganizationUserSelection = z.infer<
  typeof OrganizationUserSelection
>;

export const OrganizationGroupUserSelection = z.object({
  id: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  organizationUserId: z.string(),
  organizationGroupId: z.string(),
  organizationUser: OrganizationUserSelection,
});

export type OrganizationGroupUserSelection = z.infer<
  typeof OrganizationGroupUserSelection
>;

export const GetGroupUsersResponse = z.object({
  users: z.array(OrganizationGroupUserSelection),
});

export type GetGroupUsersResponse = z.infer<typeof GetGroupUsersResponse>;

export const GetOrganizationUsersResponse = z.object({
  users: z.array(OrganizationUserSelection),
});

export type GetOrganizationUsersResponse = z.infer<
  typeof GetOrganizationUsersResponse
>;
